/**
 * Global Cache Manager
 * Provides centralized functions for managing application-wide caching
 */

import { clearDbStore } from '../utils/indexedDBHelper'

// Known database names and stores for application-wide cache clearing
const DB_CONFIGURATIONS = [
  { dbName: 'dnd_5e_spells', storeName: 'spellDetails' },
  { dbName: 'dnd_5e_races', storeName: 'raceDetails' },
  { dbName: 'dnd_5e_classes', storeName: 'classData' },
  { dbName: 'dnd_5e_monsters', storeName: 'monsterDetails' },
  { dbName: 'dnd_5e_backgrounds', storeName: 'backgroundDetails' },
  { dbName: 'dnd_5e_abilities', storeName: 'abilityDetails' },
  { dbName: 'dnd_5e_subraces', storeName: 'subraceDetails' },
  { dbName: 'dnd_5e_conditions', storeName: 'conditionDetails' },
  { dbName: 'dnd_5e_equipment', storeName: 'equipmentDetails' },
  { dbName: 'dnd_5e_features', storeName: 'featureDetails' }
]

// Store names that implement clearCache methods
const CLEARABLE_STORES = [
  'raceStore',
  'classStore',
  'backgroundStore',
  'spellStore',
  'skillStore',
  'monsterStore',
  'weaponStore',
  'equipmentStore',
  'subraceStore',
  'abilityStore',
  'conditionStore'
]

/**
 * Clear all application caches
 * @param {Object} options - Cache clearing options
 * @param {boolean} options.memory - Whether to clear in-memory caches (true by default)
 * @param {boolean} options.localStorage - Whether to clear localStorage caches (true by default)
 * @param {boolean} options.indexedDB - Whether to clear IndexedDB caches (true by default)
 * @param {string} options.gameVersion - Specific game version to clear (all if not specified)
 * @returns {Promise<Object>} - Results of the cache clearing operations
 */
export const clearAllCaches = async (options = {}) => {
  // Default options
  const opts = {
    memory: true,
    localStorage: true,
    indexedDB: true,
    gameVersion: null,
    ...options
  }

  const results = {
    memory: { cleared: false, errors: [] },
    localStorage: { cleared: false, errors: [] },
    indexedDB: { cleared: false, errors: [] }
  }

  // Clear memory caches in Pinia stores
  if (opts.memory) {
    try {
      // Lazily import the Pinia instance to avoid build-time evaluation cycles
      const { pinia: piniaInstance } = await import('../stores/piniaInstance')
      const stores = piniaInstance?._s || null

      if (stores) {
        // Call clearCache on all known stores that implement it
        for (const storeName of CLEARABLE_STORES) {
          const storeKey = `${storeName}`
          const store = stores.get(storeKey)

          if (store && typeof store.clearCache === 'function') {
            await store.clearCache(opts.gameVersion)
          }
        }
        // Clear version-specific store cache if we're clearing all game versions
        if (!opts.gameVersion) {
          if (
            piniaInstance._a &&
            piniaInstance._a.$root &&
            piniaInstance._a.$root.__VUE_DEVTOOLS_GLOBAL_HOOK__
          ) {
            // Clear Vue 3 Pinia store cache (for development purposes only)
            piniaInstance._a.$root.__VUE_DEVTOOLS_GLOBAL_HOOK__.emit('pinia:reset')
          }
        }
      } else {
        console.warn('[CacheManager] Pinia instance not available; skipped memory cache clearing')
      }

      results.memory.cleared = true
    } catch (error) {
      console.error('Error clearing memory caches:', error)
      results.memory.errors.push(error.message)
    }
  }

  // Clear localStorage caches
  if (opts.localStorage) {
    try {
      const keys = []

      // Collect all localStorage keys starting with "cache_"
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key.startsWith('cache_')) {
          if (!opts.gameVersion || key.includes(opts.gameVersion)) {
            keys.push(key)
          }
        }
      }

      // Remove found cache entries
      keys.forEach((key) => localStorage.removeItem(key))

      results.localStorage.cleared = true
    } catch (error) {
      console.error('Error clearing localStorage caches:', error)
      results.localStorage.errors.push(error.message)
    }
  }

  // Clear IndexedDB caches
  if (opts.indexedDB) {
    try {
      const promises = []

      for (const config of DB_CONFIGURATIONS) {
        // If gameVersion is specified, only clear matching DBs
        if (opts.gameVersion) {
          if (config.dbName.includes(opts.gameVersion)) {
            promises.push(clearDbStore(config.dbName, config.storeName))
          }
        } else {
          promises.push(clearDbStore(config.dbName, config.storeName))
        }
      }

      await Promise.allSettled(promises)

      results.indexedDB.cleared = true
    } catch (error) {
      console.error('Error clearing IndexedDB caches:', error)
      results.indexedDB.errors.push(error.message)
    }
  }

  console.log('Cache clearing completed:', results)
  return results
}

/**
 * Clear caches for a specific game version
 * @param {string} gameVersion - Game version ID
 * @returns {Promise<Object>} - Results of the cache clearing operations
 */
export const clearGameVersionCaches = async (gameVersion) => {
  if (!gameVersion) {
    throw new Error('Game version ID is required')
  }

  return clearAllCaches({ gameVersion })
}

/**
 * Clear all session-related caches when a user logs out
 * This is more selective than clearing all caches
 * @returns {Promise<Object>} - Results of the cache clearing operation
 */
export const clearSessionCaches = async () => {
  // Clear user-specific caches without clearing game data caches
  const results = {
    sessionStorage: { cleared: false, errors: [] },
    userPreferences: { cleared: false, errors: [] }
  }

  try {
    // Clear session storage
    sessionStorage.clear()
    results.sessionStorage.cleared = true

    // Clear user-specific localStorage items
    const userKeys = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key.startsWith('user_') || key.startsWith('character_') || key.startsWith('campaign_')) {
        userKeys.push(key)
      }
    }

    userKeys.forEach((key) => localStorage.removeItem(key))
    results.userPreferences.cleared = true
  } catch (error) {
    console.error('Error clearing session caches:', error)
    results.userPreferences.errors.push(error.message)
  }

  return results
}

/**
 * Memory usage utilities to track memory consumption
 */
export const memoryUsage = {
  /**
   * Get current memory usage if available in browser
   * @returns {Object|null} Memory usage stats or null if not supported
   */
  getCurrentUsage() {
    if (performance && performance.memory) {
      return {
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
      }
    }
    return null
  },

  /**
   * Run garbage collection if DevTools are open
   * Note: This only works in DevTools and is for debugging only
   */
  runGarbageCollection() {
    if (window.gc) {
      try {
        window.gc()
        console.log('Manual garbage collection completed')
        return true
      } catch (e) {
        console.warn('Manual garbage collection failed:', e)
        return false
      }
    }
    console.warn(
      'Manual garbage collection not available. Open DevTools and run with --expose-gc flag.'
    )
    return false
  }
}
